import { Component } from '@opten/gin-tonic/helpers/compiler';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - typings resolution issue with swiper exports in this setup
import Swiper, { Pagination, A11y, Navigation, Autoplay } from 'swiper';

@Component({ className: 'js-testimonials' })
export class TestimonialsComponent {
	private swiper?: Swiper;

	constructor(private element: HTMLElement) {
		this.init();
	}

	private init() {
		Swiper.use([Pagination, A11y, Navigation, Autoplay]);

		// Find navigation elements (arrows) if present
		// Use the closest `.c-testimonials` wrapper and collect all next/prev children
		const parent = this.element.parentElement as HTMLElement;
		const container = (this.element as HTMLElement).closest(
			'.c-testimonials'
		) as HTMLElement | null;
		const nextEls = container?.querySelectorAll('.swiper-button-next') as
			| NodeListOf<HTMLElement>
			| undefined;
		const prevEls = container?.querySelectorAll('.swiper-button-prev') as
			| NodeListOf<HTMLElement>
			| undefined;
		const nextEl = nextEls && nextEls.length ? Array.from(nextEls) : undefined;
		const prevEl = prevEls && prevEls.length ? Array.from(prevEls) : undefined;
		// Determine number of slides to tune looping/duplication
		const slidesCount = (
			this.element as unknown as HTMLElement
		).querySelectorAll('.swiper-slide').length;

		// pagination element (used also for hover pause)
		const paginationEl = (
			this.element.parentElement as HTMLElement
		).querySelector('.swiper-pagination') as HTMLElement | undefined;

		this.swiper = new Swiper(this.element as unknown as HTMLElement, {
			// Only loop if there are multiple slides, and duplicate enough to allow smooth swiping
			loop: slidesCount > 1,
			loopPreventsSliding: false, // allow sliding even if slides < slidesPerView
			loopedSlidesLimit: false, // don't limit duplicates to slidesPerView
			loopedSlides: slidesCount, // duplicate all to be safe
			watchOverflow: true,
			centeredSlides: true,
			// Mobile-first defaults
			slidesPerView: 1,
			spaceBetween: 20,
			// Keep swiper responsive
			breakpoints: {
				// Desktop/tablet
				992: {
					slidesPerView: 1.5,
					spaceBetween: 32,
				},
				1200: {
					slidesPerView: 2.3,
				},
			},
			observeParents: true,
			observer: true,
			// Autoplay: only when there are multiple slides
			autoplay:
				slidesCount > 1
					? {
							delay: 5000,
							disableOnInteraction: false,
							// we'll handle pause on hover manually so keep internal pause off
							pauseOnMouseEnter: false,
					  }
					: undefined,
			navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
			pagination:
				paginationEl && slidesCount > 1
					? {
							el: paginationEl,
							clickable: true,
							bulletElement: 'span',
							bulletClass: 'swiper-pagination-bullet',
							bulletActiveClass: 'swiper-pagination-bullet-active',
					  }
					: undefined,
			a11y: {
				prevSlideMessage: 'Previous',
				nextSlideMessage: 'Next',
			},
		});

		// Pause/resume autoplay when hovering or touching certain elements
		const stopAutoplay = () => (this.swiper as any)?.autoplay?.stop?.();
		const startAutoplay = () => (this.swiper as any)?.autoplay?.start?.();

		// container/section to watch for hover
		const sectionEl = parent?.parentElement?.parentElement?.querySelector(
			'.js-swiper-hover-pause'
		) as HTMLElement | undefined;

		sectionEl?.addEventListener('mouseenter', stopAutoplay);
		sectionEl?.addEventListener('mouseleave', startAutoplay);
		// touch devices: stop on touchstart, resume after touchend
		sectionEl?.addEventListener('touchstart', stopAutoplay, { passive: true });
		sectionEl?.addEventListener('touchend', () => {
			// small timeout to avoid immediate restart while user interacts
			setTimeout(startAutoplay, 2000);
		});

		// Allow clicking the adjacent slides (the ones with
		// `swiper-slide-next` or `swiper-slide-prev`) to navigate directly
		// to that slide. This handles both duplicated looped slides (via
		// `data-swiper-slide-index`) and normal slides.
		this.element.addEventListener('click', (ev: MouseEvent) => {
			const target = ev.target as HTMLElement | null;
			if (!target) return;
			const clickedSlide = target.closest(
				'.swiper-slide-next, .swiper-slide-prev'
			) as HTMLElement | null;
			if (!clickedSlide) return;

			// Ensure the clicked slide belongs to this swiper instance
			if (!this.element.contains(clickedSlide)) return;

			// Use Swiper's simple next/prev API for adjacent-slide clicks
			if (clickedSlide.classList.contains('swiper-slide-next')) {
				(this.swiper as any)?.slideNext?.();
			} else if (clickedSlide.classList.contains('swiper-slide-prev')) {
				(this.swiper as any)?.slidePrev?.();
			}
		});
	}
}

(function init() {
	const nodes = document.querySelectorAll<HTMLElement>(
		'[data-component="testimonials"] .js-testimonials'
	);
	nodes.forEach((el) => new TestimonialsComponent(el));
})();
